// Types shared between the SPA and Pages Functions backend.
// Keep this file dependency-free so it imports cleanly in both environments.

export type ArtworkStatus = 'pending' | 'ready' | 'failed';

export interface CategoryRef {
  id: string;
  name: string;
}

export interface TagRef {
  id: string;
  name: string;
}

export interface ArtworkPublic {
  id: string;
  title: string;
  description: string | null;
  category: CategoryRef;
  tags: TagRef[];
  thumb_url: string;
  full_url: string;
  thumb_width: number | null;
  thumb_height: number | null;
  full_width: number | null;
  full_height: number | null;
  created_at: string; // ISO
}

export interface ArtworkAdmin extends ArtworkPublic {
  status: ArtworkStatus;
  thumb_key: string;
  full_key: string;
}

export interface ImagesPage {
  items: ArtworkPublic[];
  next_cursor: string | null;
}

export interface MetaPayload {
  categories: Array<CategoryRef & { count: number }>;
  tags: Array<TagRef & { count: number }>;
}

export interface UploadRequest {
  title: string;
  description?: string | null;
  category: string; // name; resolved-or-created server-side
  tags: string[]; // names; resolved-or-created server-side
  thumb: { content_type: string; width: number; height: number; size: number };
  full: { content_type: string; width: number; height: number; size: number };
}

export interface UploadResponse {
  artwork_id: string;
  thumb: PresignedUpload;
  full: PresignedUpload;
}

export interface PresignedUpload {
  url: string;
  key: string;
  method: 'PUT';
  required_headers: Record<string, string>;
  expires_in_seconds: number;
}

export interface UpdateArtworkRequest {
  title?: string;
  description?: string | null;
  category?: string;
  tags?: string[];
  // Optional: signal that the user wants to replace images. Server returns
  // new presigned PUTs and waits for /complete.
  replace_images?: {
    thumb: { content_type: string; width: number; height: number; size: number };
    full: { content_type: string; width: number; height: number; size: number };
  };
}

export interface UpdateArtworkResponse {
  artwork: ArtworkAdmin;
  // Present only when replace_images was requested.
  thumb_upload?: PresignedUpload;
  full_upload?: PresignedUpload;
}

export interface CompleteUploadRequest {
  thumb_width?: number;
  thumb_height?: number;
  full_width?: number;
  full_height?: number;
}

export interface ApiError {
  error: string;
  message: string;
  request_id?: string;
}

// Hard caps used by both client and server. Keep in sync with docs/spec.
export const IMAGE_LIMITS = {
  MAX_SOURCE_BYTES: 25 * 1024 * 1024, // 25 MB raw upload limit
  MAX_DERIVATIVE_BYTES: 8 * 1024 * 1024, // 8 MB cap per derivative after compression
  THUMB_MAX_PX: 400,
  FULL_MAX_PX: 2000,
  THUMB_QUALITY: 0.8,
  FULL_QUALITY: 0.85,
  ALLOWED_INPUT_TYPES: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ] as const,
  OUTPUT_TYPE: 'image/webp' as const,
} as const;

export const PAGINATION = {
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 50,
} as const;

export const PRESIGN = {
  EXPIRES_IN_SECONDS: 600, // 10 minutes
} as const;
